import MainLayout from "components/layout/main-layout/MainLayout";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import DatePicker from "react-datepicker";
import { titles } from "constants/titles";
import React, { useEffect, useRef, useState } from "react";
import "./LocationHistory.css";
import { ArrowRight, BackIcon, IconCalender } from "assets/SVGs/SVGs";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { getBreadcrumData } from "redux/Devices/actions";
import { useHistory } from "react-router-dom";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { loader_gif } from "assets/images/images";
import { convertDateForLocation } from "utility";

const LocationHistory = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [locationTime, setLocationTime] = useState("");
  const userData = useSelector((store) => store?.auth?.userData);
  const { selectedDeviceDetails, breadcrumData, breadCrumloading } =
    useSelector((store) => store?.devices);
  const mapRef = useRef(null);
  const history = useHistory();
  const dateRef = useRef();
  const dispatch = useDispatch();

  // Initialize validation hook
  const { onApiResponse, snackbarState, dismissSnackbar } = useAdvancedValidation();

  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  };

  const handleChange = (date) => {
    setSelectedDate(date);
    fetchTrackingDetail(date);
  };

  const fetchTrackingDetail = (date) => {
    const orignalDate = new Date(date);
    const time = orignalDate.toLocaleTimeString("en-IN", timeOptions);
    setLocationTime(time);
    const formattedDate = orignalDate.toISOString().split("T")[0];
    const deviceType = selectedDeviceDetails?.device_type?.trim();
    const timeZone = moment.tz.guess();
    const payload = {
      user_device_id: selectedDeviceDetails?.user_device_id,
      medical_alert_user_id: selectedDeviceDetails?.medical_alert_user_id,
      date: formattedDate,
      timezone: timeZone,
    };

    getBreadcrumData({
      dispatch,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      payload: payload,
      deviceType,
    });
  };

  const handleNextMonth = () => {
    if (selectedDate) {
      if (
        moment(selectedDate)?.format("YYYY-MM-DD") !==
        moment().format("YYYY-MM-DD")
      ) {
        const previousDate = moment(selectedDate)?.add(1, "days").toDate();
        setSelectedDate(previousDate);
        fetchTrackingDetail(moment(previousDate)?.format("YYYY-MM-DD"));
      } else {
        onApiResponse("You cannot select future dates", "error");
      }
    }
  };

  const handlePrevDay = () => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate?.setDate(prevDate?.getDate() - 1);
      return newDate;
    });
  };

  // Add event listeners immediately when map container exists to prevent initial zoom
  useEffect(() => {
    const mapContainer = mapRef.current;
    if (!mapContainer) return;

    let mapReady = false;

    const readyTimeout = setTimeout(() => {
      mapReady = true;
    }, 2000);

    const handleWheel = (e) => {
      const isOnMap = mapContainer?.contains(e?.target) ||
        e?.target?.closest('.lh_map_div') ||
        e?.target?.closest('.gm-style') ||
        e?.target?.closest('[class*="gm-"]');

      if (isOnMap && !e?.ctrlKey && !e?.metaKey) {
        e?.stopPropagation();
        if (!mapReady) {
          e?.preventDefault();
        }
      }
    };

    mapContainer?.addEventListener('wheel', handleWheel, { passive: false, capture: true });

    const documentHandleWheel = (e) => {
      const mapContainer = mapRef?.current;
      if (mapContainer) {
        const isOnMap = mapContainer?.contains(e?.target) ||
          e?.target?.closest('.lh_map_div') ||
          e?.target?.closest('.gm-style') ||
          e?.target?.closest('[class*="gm-"]');

        if (isOnMap && !e?.ctrlKey && !e?.metaKey) {
          e?.stopPropagation();
          if (!mapReady) {
            e?.preventDefault();
          }
        }
      }
    };

    // Add document listener with a timeout to remove it after map loads
    document?.addEventListener('wheel', documentHandleWheel, { passive: false, capture: true });

    // Remove document listener after 3 seconds (enough time for map to load)
    const timeoutId = setTimeout(() => {
      document?.removeEventListener('wheel', documentHandleWheel, { capture: true });
    }, 3000);

    return () => {
      clearTimeout(readyTimeout);
      clearTimeout(timeoutId);
      mapContainer?.removeEventListener('wheel', handleWheel, { capture: true });
      document?.removeEventListener('wheel', documentHandleWheel, { capture: true });
    };
  }, [breadcrumData]); // Re-add when breadcrumData changes (map container re-renders)

  useEffect(() => {
    let cleanupFunctions = [];
    let intervalId = null;

    const loadMap = () => {
      if (!window?.google || !mapRef?.current || breadcrumData?.length === 0)
        return;

      const map = new window.google.maps.Map(mapRef.current, {
        center: {
          lat: Number(breadcrumData?.[0]?.latitude),
          lng: Number(breadcrumData?.[0]?.longitude),
        },
        zoom: 15,
        zoomControl: true, // Enable zoom controls (+/- buttons)
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER
        },
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy', // Map captures all scroll events, prevents browser zoom
        disableDoubleClickZoom: false,
        scrollwheel: true, // Enable scroll wheel zoom on map
      });

      const latestLocation = breadcrumData?.reduce(
        (latest, current) =>
          new Date(current?.timestamp) > new Date(latest?.timestamp)
            ? current
            : latest,
        breadcrumData[0]
      );

      breadcrumData.forEach((location, index) => {
        if (location?.latitude && location?.longitude) {
          const markerPosition = {
            lat: Number(location?.latitude),
            lng: Number(location?.longitude),
          };

          const marker = new window.google.maps.Marker({
            position: markerPosition,
            map: map,
            title: "Click to see more",
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `<div style="font-weight:bold;padding-top:8px">
                        <span>Location:</span>
                        <span>${index + 1}</span>
                      </div>
                      <div>${convertDateForLocation(location?.timestamp)}</div>`,
          });

          marker.addListener("click", () => {
            infoWindow.open(map, marker);
          });

          if (location?.timestamp === latestLocation?.timestamp) {
            setTimeout(() => infoWindow.open(map, marker), 500);
          }
        }
      });
    };

    if (window?.google) {
      loadMap();
    } else {
      intervalId = setInterval(() => {
        if (window?.google) {
          loadMap();
          clearInterval(intervalId);
        }
      }, 500);
    }

    // Cleanup function for useEffect
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      // Run all cleanup functions
      cleanupFunctions?.forEach(cleanup => cleanup());
      cleanupFunctions = [];
    };
  }, [selectedDate, breadcrumData]);

  useEffect(() => {
    const currentDate = selectedDate;
    const formattedDate = currentDate?.toISOString()?.split("T")?.[0];
    fetchTrackingDetail(formattedDate);
  }, [selectedDate]);

  const CustomInput = ({ value, onClick }) => {
    const isToday = moment(selectedDate)?.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
    const displayDate = isToday ? "Today" : moment(selectedDate)?.format("YYYY-MM-DD");

    return (
      <div className="lh_date_selector">
        <button
          className="lh_arrow_button"
          onClick={handlePrevDay}
          aria-label="Previous day"
        >
          <BackIcon height={18} />
        </button>

        <button className="lh_date_display" onClick={onClick}>
          <span className="lh_date_text">{displayDate}</span>
          <span className="lh_calendar_icon">
            <IconCalender fillColor={'#fff'} />
          </span>
        </button>

        <button
          className="lh_arrow_button"
          onClick={handleNextMonth}
          aria-label="Next day"
        >
          <ArrowRight color="#fff" height={18} />
        </button>
      </div>
    );
  };
  return (
    <MainLayout
      text={titles?.location_history}
      isBackHide={false}
      isNavbar={true}
      white={true}
      fullWidthScreen={true}
      primary={true}
      isLocationHistory={true}
    >
      {/* Snackbar - always sticky below header */}
      {snackbarState?.show && (
        <div className="snackbar_sticky">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarState?.autoHideDuration}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}

      <div className="lh_content_container">
        {breadCrumloading ? (
          <div className="gif_loader_container">
            <img height="50px" width="50px" src={loader_gif} alt="loader" />
          </div>
        ) : null}

        <div className="lh_date_picker_container">
          <DatePicker
            ref={dateRef}
            onChange={(date) => handleChange(date)}
            selected={selectedDate}
            dateFormat="yyyy/MM/dd"
            placeholderText="MM/DD/YYYY"
            customInput={<CustomInput value={selectedDate} />}
            maxDate={new Date()}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
          />
        </div>

        {breadcrumData?.length > 0 ? (
          <div className="lh_map_container">
            <div id="map" ref={mapRef} className="lh_map_div" />
          </div>
        ) : (
          <div className="lh_empty_state">
            <h4 className="lh_empty_message display_h4 text-white-color text-center">{titles?.no_location_detail}</h4>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LocationHistory;
